import React from 'react';
import { getDurationInHoursAndMinutes } from '@teetsh/app/src/utils/time/getDurationInHoursAndMinutes';
import {
  bgColors,
  getTextColorFromBackgroundColor,
} from '@teetsh/app/src/components/colors';
import tw from 'twin.macro';
import { Color } from '@teetsh/app/src/types/colors';
import { useTranslation } from 'react-i18next';
import Disclosure from 'components/Disclosure';
import { Details, Element } from './Details';

export interface Item {
  id: string;
  minutes: number;
  name: string;
  color: Color;
  elements?: Element[];
}

interface Group {
  id: string;
  name: string;
  items: Item[];
}

interface Props {
  groups: Group[];
}

export function TagList(props: Props) {
  const { groups } = props;
  const { t } = useTranslation(['common']);
  return (
    <div tw="space-y-8">
      {groups.map(group => (
        <div key={group.id}>
          {groups.length > 1 && <h4 tw="mb-2">{group.name}</h4>}
          <div>
            {group.items.map(item => {
              const { color, minutes, name, id } = item;
              return (
                <div key={id} css={[tw`border-b border-neutral-quiet`]}>
                  <Disclosure fill>
                    <Disclosure.Button
                      alignText="left"
                      fill
                      tw="px-2 py-3 hover:bg-neutral-supershy hover:rounded-none"
                      iconRotation="bottom-to-top"
                      iconPosition="right"
                      small
                      minimal
                    >
                      <div
                        key={id}
                        tw="flex flex-grow justify-between items-center pr-2 min-w-0"
                      >
                        <div
                          css={[
                            tw`mr-1 rounded-lg py-0.5 px-2 text-sm truncate`,
                            bgColors(color),
                            getTextColorFromBackgroundColor(color),
                          ]}
                        >
                          {name}
                        </div>
                        <div tw="ml-2 text-base font-normal">
                          {getDurationInHoursAndMinutes(minutes, {
                            leadingZero: true,
                            short: true,
                            noMinuteSuffixWhenHours: true,
                          })}
                        </div>
                      </div>
                    </Disclosure.Button>
                    <Disclosure.Panel>
                      <Details elements={item?.elements || []} />
                    </Disclosure.Panel>
                  </Disclosure>
                </div>
              );
            })}
          </div>
          {group.items.length > 0 && (
            <div tw="flex justify-between px-2 py-3">
              <h4>{t('common:components.tags.total')}</h4>
              <h4>
                {getDurationInHoursAndMinutes(
                  group.items.reduce((acc, num) => acc + num.minutes, 0),
                  {
                    leadingZero: true,
                    short: true,
                  },
                )}
              </h4>
            </div>
          )}
        </div>
      ))}
    </div>
  );
}
